import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { orderMode, STEPS, fmtDT, CARRIER_NAMES } from "./orderMeta";
import { OrderTimeline, ShipmentHistory } from "./OrderTimeline";
import { AddressBlock, AddressForm } from "./AddressForm";
import { ShipmentForm } from "./SellerDeliveryPanel";

export function AdminOrderTools({ o, reload }) {
  const { prompt } = useConfirm();
  const [panel, setPanel] = useState(null);
  const [audit, setAudit] = useState(null);
  const mode = orderMode(o);
  const final = ["completed", "cancelled", "refunded"].includes(o.status);
  useEffect(() => { if (panel === "audit") api.get(`/admin/marketplace/orders/${o.id}/audit`).then((r) => setAudit(r.data)).catch(() => setAudit([])); }, [panel, o.id]);

  const setStatus = async (status) => {
    const reason = await prompt({ title: `Corrigir estado para «${STEPS[mode].find(([s]) => s === status)?.[1] || status}»`, description: "Motivo obrigatório — fica registado no audit log.", placeholder: "Motivo", required: true, confirmLabel: "Corrigir" });
    if (!reason) return;
    try { await api.post(`/admin/marketplace/orders/${o.id}/status`, { status, reason }); toast.success("Estado corrigido"); reload?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const targets = STEPS[mode].map(([s]) => s).filter((s) => s !== "paid" && s !== o.status);

  return (
    <div className="mt-2 pt-2 border-t border-zinc-100 space-y-2" data-testid={`admin-order-tools-${o.order_number}`}>
      <div className="text-[11px] text-zinc-600 flex flex-wrap gap-x-3">
        <span>Modo: <b>{mode === "delivery" ? "Entrega" : "Levantamento"}</b></span>
        {mode === "delivery" && <span>Morada: <b>{o.shipping_address_status || "—"}</b> ({o.shipping_address_source || "—"})</span>}
        {o.shipment?.carrier && <span>Envio: <b>{CARRIER_NAMES[o.shipment.carrier] || o.shipment.carrier}</b>{o.shipment.tracking_number && ` · ${o.shipment.tracking_number}`}</span>}
        {o.confirmed_by && <span>Confirmado por {o.confirmed_by} {fmtDT(o.confirmed_at)}</span>}
      </div>
      <OrderTimeline o={o} />
      {mode === "delivery" && <AddressBlock o={o} testid={`admin-address-${o.order_number}`} />}
      {!final && (
        <div className="flex flex-wrap gap-1">
          {targets.map((s) => <button key={s} data-testid={`admin-status-${s}-${o.order_number}`} onClick={() => setStatus(s)} className="text-[11px] font-semibold px-2 py-1.5 rounded-lg bg-zinc-100 hover:bg-zinc-200">→ {STEPS[mode].find(([x]) => x === s)[1]}</button>)}
          {mode === "delivery" && <button data-testid={`admin-shipment-${o.order_number}`} onClick={() => setPanel(panel === "ship" ? null : "ship")} className="text-[11px] font-semibold px-2 py-1.5 rounded-lg bg-zinc-800 text-white">Corrigir envio</button>}
          {mode === "delivery" && ["paid", "processing"].includes(o.status) && <button data-testid={`admin-address-edit-${o.order_number}`} onClick={() => setPanel(panel === "addr" ? null : "addr")} className="text-[11px] font-semibold px-2 py-1.5 rounded-lg bg-zinc-800 text-white">Corrigir morada</button>}
        </div>
      )}
      {final && <p className="text-[11px] text-zinc-400">Encomenda {o.status} — não pode ser reaberta.</p>}
      <button data-testid={`admin-audit-${o.order_number}`} onClick={() => setPanel(panel === "audit" ? null : "audit")} className="text-[11px] underline text-zinc-500">Histórico de alterações</button>
      {panel === "ship" && <ShipmentForm o={o} reload={reload} admin onSaved={() => setPanel(null)} />}
      {panel === "addr" && <AddressForm order={o} admin onDone={() => { setPanel(null); reload?.(); }} onCancel={() => setPanel(null)} />}
      {panel === "audit" && (
        <div data-testid={`admin-audit-list-${o.order_number}`} className="text-[11px] space-y-1">
          <div className="font-semibold">Histórico da encomenda</div>
          {(Array.isArray(o.history) ? o.history : []).slice().reverse().map((h, i) => <div key={i} className="text-zinc-600"><span className="text-zinc-400">{fmtDT(h.at)}</span> · {h.status}{h.by_role ? ` (${h.by_role})` : ""}{h.note ? ` — ${h.note}` : ""}</div>)}
          <div className="font-semibold mt-1">Audit log (admin)</div>
          {audit === null ? <div className="text-zinc-400">A carregar…</div> : audit.length === 0 ? <div className="text-zinc-400">Sem correções de admin.</div>
            : audit.map((a) => <div key={a.id} className="text-zinc-600"><span className="text-zinc-400">{fmtDT(a.created_at)}</span> · {a.action} — {a.reason}</div>)}
          <ShipmentHistory o={o} testid={`admin-shipment-history-${o.order_number}`} />
        </div>
      )}
    </div>
  );
}
